import { useContext } from 'react';
import { useQuery } from '../../../react-query';
import moment from 'moment';
import { riskLimitsApi, snaSooApi } from '../../../services/api';
import { DateContext } from '../../../context/DateContext';

const QUERY_KEY = 'firmwideSummary';

// The seven strategy types shown as cards. A type with no live row still gets a card (all zeros).
const STRATEGY_TYPES = [
  'MERGER ARBITRAGE',
  'EDFI',
  'SPAC LIQUIDATION ARBITRAGE',
  'CONVERTIBLE ARBITRAGE',
  'CREDIT ARBITRAGE',
  'VOL ARBITRAGE',
  'STAT ARBITRAGE',
];

// Helper to get latest valuation date from DateContext
function useLatestValuationDate() {
  const { data: dateData } = useContext(DateContext);
  return dateData?.latestValuationDate
    ? moment(dateData.latestValuationDate, 'MMM DD, YYYY').format('YYYY-MM-DD')
    : null;
}

const limitPct = (value) => (value !== null && value !== undefined ? Number(value) : 100);

const toStrategy = (strategyTypeName, row = {}, extras = {}) => {
  const lmv = Number(row.lmv) || 0;
  const smv = Number(row.smv) || 0;
  const gmv = Number(row.gross_exposure ?? lmv + smv);
  const nmv = Number(row.net_exposure ?? lmv - smv);
  const riskCapital = Number(row.risk_capital) || 0;

  return {
    strategy_type_name: strategyTypeName,
    lmv,
    smv,
    gmv,
    nmv,
    risk_capital: riskCapital,
    risk_capital_utilization: riskCapital && gmv ? gmv / riskCapital : 0,
    // Max exposure thresholds (from API - if null/undefined, use 100 as default)
    max_lmv_pct: limitPct(row.max_lmv_pct),
    // SPAC warrant/right LMV/RC limit (null for other strategy types)
    max_wt_rt_lmv_pct: row.max_wt_rt_lmv_pct ?? null,
    max_smv_pct: limitPct(row.max_smv_pct),
    max_gmv_pct: limitPct(row.max_gmv_pct),
    max_nmv_pct: limitPct(row.max_nmv_pct),
    // Live Monte Carlo VaR from Risk Monitor; null renders as "-".
    var95: row.var_95 ?? null,
    var99: row.var_99 ?? null,
    // VaR thresholds: % of allocated risk capital as entered, and the dollar limit -(pct x RC) (negative like VaR).
    var95Pct: row.var_95_pct ?? null,
    var99Pct: row.var_99_pct ?? null,
    var95Threshold: row.var_95_threshold ?? null,
    var99Threshold: row.var_99_threshold ?? null,
    // This type's strategies ranked by GMV (lmv + smv), from /risk-limits/utilization.
    top5StrategiesBySize: Array.isArray(row.top_strategies) ? row.top_strategies : [],
    // Merger Arbitrage deal break risk and the SPAC tables (risk schema, loaded from the Glazer app archives).
    // Empty when no day is loaded; the card then hides those sections.
    top5DealBreakRisk: Array.isArray(extras.dealBreak?.data)
      ? extras.dealBreak.data.map((r) => ({ strategy_name: r.strategy_name, dealBreakRisk: r.deal_break_risk }))
      : [],
    top5SpacCommon: extras.spac?.common ?? [],
    top5SpacWarrant: extras.spac?.warrant ?? [],
    top5SpacRight: extras.spac?.right ?? [],
  };
};

// One entry per strategy type, built only from the live /risk-limits/utilization rows.
const mergeStrategyData = (apiRows = [], extras = {}) => {
  const rowsByType = new Map(
    (Array.isArray(apiRows) ? apiRows : [])
      .filter((row) => row?.strategy_type_name)
      .map((row) => [row.strategy_type_name, row])
  );
  return STRATEGY_TYPES.map((name) => toStrategy(name, rowsByType.get(name), extras));
};

// Calculate firmwide metrics from strategies. AUM is the live NAV as of the selected date; with no
// NAV, AUM / Reserve / as-of are null and render as "-". VaR is live from Risk Monitor or null.
const calculateFirmwideMetrics = (strategies = [], navData = null, varData = null) => {
  const totalRiskCapitalAllocated = strategies.reduce(
    (sum, s) => sum + (s.risk_capital || 0),
    0
  );

  const liveNav = Number(navData?.total_nav);
  const hasLiveNav = Number.isFinite(liveNav) && liveNav > 0;

  return {
    aum: hasLiveNav ? liveNav : null,
    totalRiskCapitalAllocated,
    reserve: hasLiveNav ? liveNav - totalRiskCapitalAllocated : null,
    var95: Number.isFinite(varData?.var_95) ? varData.var_95 : null,
    var99: Number.isFinite(varData?.var_99) ? varData.var_99 : null,
    asOfDate: hasLiveNav ? navData.as_of : null,
    varAsOfDate: varData?.valuation_date || null,
  };
};

// Main hook for fetching firmwide summary data
export const useFirmwideSummaryData = (selectedDate = null) => {
  const latestDate = useLatestValuationDate();
  const effectiveDate = selectedDate || latestDate;

  console.log('useFirmwideSummaryData - selectedDate:', selectedDate, 'latestDate:', latestDate, 'effectiveDate:', effectiveDate);

  // Fetch utilization data from /risk-limits/utilization endpoint
  const query = useQuery(
    [QUERY_KEY, 'utilization', effectiveDate],
    async () => {
      console.log('📡 Fetching from /risk-limits/utilization with date:', effectiveDate);
      const response = await riskLimitsApi.getUtilization({
        start_date: effectiveDate,
        end_date: effectiveDate,
      });
      console.log('📦 API Response:', response);
      return response?.data || [];
    },
    {
      enabled: Boolean(effectiveDate),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  // Live NAV as of the same date — drives AUM and Reserve. A failure here
  // shouldn't blank the page, so it falls back to the dummy AUM instead.
  const navQuery = useQuery(
    [QUERY_KEY, 'nav', effectiveDate],
    () => snaSooApi.getNavByDate(effectiveDate),
    {
      enabled: Boolean(effectiveDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  // Firmwide VaR from Risk Monitor, same date. Not retried and not part of
  // isLoading — a slow/unavailable VaR service shouldn't hold up the page.
  const varQuery = useQuery(
    [QUERY_KEY, 'firmwide-var', effectiveDate],
    () => riskLimitsApi.getFirmwideVar({ start_date: effectiveDate, end_date: effectiveDate }),
    {
      enabled: Boolean(effectiveDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // Portfolio greeks from Risk Monitor, same date. Not retried and not part of
  // isLoading — a slow/unavailable greeks service shouldn't hold up the page.
  const greeksQuery = useQuery(
    [QUERY_KEY, 'portfolio-greeks', effectiveDate],
    () => {
      console.log('Fetching portfolio Greeks with params:', { start_date: effectiveDate, end_date: effectiveDate });
      return riskLimitsApi.getPortfolioGreeks({ start_date: effectiveDate, end_date: effectiveDate });
    },
    {
      enabled: Boolean(effectiveDate),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // Merger Arbitrage deal break risk and SPAC tables for the same date. Not retried and not part of
  // isLoading: if they are missing the card simply hides those sections.
  const dealBreakQuery = useQuery(
    [QUERY_KEY, 'deal-break-risk', effectiveDate],
    () => riskLimitsApi.getDealBreakRisk({ start_date: effectiveDate, end_date: effectiveDate }),
    { enabled: Boolean(effectiveDate), staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, retry: false }
  );
  const spacQuery = useQuery(
    [QUERY_KEY, 'spac-risk', effectiveDate],
    () => riskLimitsApi.getSpacRisk({ start_date: effectiveDate, end_date: effectiveDate }),
    { enabled: Boolean(effectiveDate), staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, retry: false }
  );

  // Merge and calculate data
  const strategies = mergeStrategyData(query.data, { dealBreak: dealBreakQuery.data, spac: spacQuery.data });
  const firmwide = calculateFirmwideMetrics(strategies, navQuery.data, varQuery.data);

  return {
    firmwide,
    strategies,
    portfolioGreeks: greeksQuery.data || null,
    // No effective date yet (valuation dates still loading) counts as loading, so the
    // page shows its skeleton instead of empty cards while the queries are still disabled.
    isLoading: !effectiveDate || query.isLoading || navQuery.isLoading,
    isFetching: query.isFetching || navQuery.isFetching,
    varLoading: varQuery.isLoading || !effectiveDate,
    isError: query.isError,
    error: query.error,
    asOfDate: effectiveDate,
  };
};
